import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface FkComboboxOption {
  value: string | number;
  label: string;
}

interface Props {
  options: FkComboboxOption[];
  value: string | number | null | undefined;
  onChange: (v: string | number | null) => void;
  placeholder?: string;
  allowNull?: boolean;
  allowFreeForm?: boolean;
}

/**
 * Single-value FK picker. Plain Popover + filtered button list — does NOT
 * use cmdk because the Command/CommandList combination silently dropped
 * options in admin's render context (only the currently-selected entry
 * showed up). The simpler implementation here is more reliable and gives
 * us full control over filtering and free-form fallback.
 */
export function FkCombobox({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  allowNull = true,
  allowFreeForm = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const triggerLabel = selected ? selected.label : value != null ? String(value) : placeholder;

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-2.5 text-sm font-normal hover:bg-muted',
            !selected && 'text-muted-foreground'
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-1" align="start">
        <Input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="h-8 mb-1"
        />
        <div className="max-h-72 overflow-y-auto flex flex-col">
          {allowNull && (
            <OptionButton
              onClick={() => {
                onChange(null);
                close();
              }}
              selected={value == null}
            >
              <span className="italic text-muted-foreground">NULL</span>
            </OptionButton>
          )}
          {filtered.length === 0 ? (
            allowFreeForm && search.trim() ? (
              <OptionButton
                onClick={() => {
                  onChange(search.trim());
                  close();
                }}
                selected={false}
              >
                <span>
                  Use "<strong>{search.trim()}</strong>" as new value
                </span>
              </OptionButton>
            ) : (
              <div className="py-3 text-center text-sm text-muted-foreground">No matches.</div>
            )
          ) : (
            filtered.map((o) => (
              <OptionButton
                key={String(o.value)}
                onClick={() => {
                  onChange(o.value);
                  close();
                }}
                selected={o.value === value}
              >
                {o.label}
              </OptionButton>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OptionButton({
  children,
  onClick,
  selected,
}: {
  children: React.ReactNode;
  onClick: () => void;
  selected: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted',
        selected && 'bg-muted/60 font-medium'
      )}
    >
      <Check className={cn('h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
      <span className="truncate flex-1">{children}</span>
    </button>
  );
}
